import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router";

import { asMarket, asRiskLevel, RobotAvatar, robotColor, useRobot } from "@/entities/robot";
import { useWallet } from "@/entities/wallet";
import { useBuyRobot } from "@/features/buy-robot";
import { classify } from "@/shared/api";
import { ROUTES } from "@/shared/config";
import { formatMoney, formatPercent, formatPrice, moneySign } from "@/shared/lib";
import { Button, Checkbox, useToast } from "@/shared/ui";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";

/**
 * Подтверждение. Figma: `Robots · Launch Confirmation` (22:207).
 *
 * Таблица условий в макете перечисляет подписку, performance fee и триггеры.
 * Подписки и триггеров в продукте нет (§6 и §11 ТЗ), а вот доля платформы в
 * прибыли появилась — она в таблице и стоит. Остальные строки заменены на
 * реальные условия сделки; уцелела одна строка макета — «Вывод средств:
 * всегда контролируете вы».
 *
 * Чекбокс один, хотя в макете их два: API принимает единственный флаг
 * `accepted_risks` (DESIGN.md §8).
 */
export function BuyConfirmPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams();
  const location = useLocation();
  const robotId = Number(id);

  // Сумма приезжает состоянием роута с шага ввода — так же, как результат
  // покупки едет на экран успеха. При перезагрузке страницы её нет, и
  // возвращаться надо на шаг ввода, а не запускать робота на неизвестную сумму.
  const state: unknown = location.state;
  const amount =
    state !== null && typeof state === "object" && "amount" in state
      ? String(state.amount)
      : undefined;

  const [accepted, setAccepted] = useState(false);
  // Ошибка суммы от бэкенда: минимум живёт в админке и меняется, поэтому
  // сообщение показывается его словами, а не пересказом на клиенте.
  const [amountError, setAmountError] = useState<string | undefined>(undefined);
  const robot = useRobot(robotId);
  const wallet = useWallet();
  const buy = useBuyRobot(robotId);

  if (amount === undefined) {
    return (
      <ScreenState
        tone="info"
        icon="questionMark"
        title={t("buy.noAmount.title")}
        body={t("buy.noAmount.body")}
        action={{
          label: t("buy.noAmount.action"),
          onClick: () => void navigate(ROUTES.robotBuy(robotId), { replace: true }),
        }}
      />
    );
  }

  if (robot.isPending || wallet.isPending) return <ScreenSkeleton blocks={[138, 184, 80]} />;

  if (robot.isError || wallet.isError) {
    return (
      <ScreenState tone="loss" icon="warningFill" title={t("states.error.title")} body={t("states.error.body")} />
    );
  }

  const color = robotColor(robot.data);

  const submit = () => {
    setAmountError(undefined);
    buy.mutate(
      { amount, acceptedRisks: accepted },
      {
      onSuccess: (result) => {
        void navigate(ROUTES.robotBuySuccess(robotId), { replace: true, state: { result } });
      },
      onError: (error) => {
        const reaction = classify(error);

        if (reaction.kind === "insufficient") {
          // Денег не хватает — это не тост, а переход к пополнению:
          // ради этого INSUFFICIENT_FUNDS и выделен отдельным кодом.
          void navigate(ROUTES.depositAsset, {
            state: { returnTo: ROUTES.robotBuyConfirm(robotId), shortfall: reaction },
          });
          return;
        }

        if (reaction.kind === "conflict") {
          toast.show({
            tone: "loss",
            title: t(reaction.inProgress ? "buy.inProgress" : "buy.alreadyDone"),
            meta: t("buy.checkMyRobots"),
          });
          return;
        }

        if (reaction.kind === "inline") {
          // Сумма ниже минимума. Текст — от бэкенда: минимум правится в
          // админке, и пересказывать его на клиенте значило бы завести второй
          // источник правды.
          setAmountError(reaction.message);
          return;
        }

        toast.show({ tone: "loss", title: t("states.error.title"), meta: reaction.kind });
        },
      },
    );
  };

  const terms: { label: string; value: string }[] = [
    { label: t("buy.terms.amount"), value: formatPrice(amount, { suffix: "USDT" }) },
    { label: t("buy.terms.minAmount"), value: formatPrice(robot.data.min_amount, { suffix: "USDT" }) },
    { label: t("buy.terms.balance"), value: formatMoney(wallet.data.balance, { suffix: "USDT" }) },
    { label: t("buy.terms.maxDd"), value: formatPercent(robot.data.declared_max_dd) },
    {
      label: t("buy.terms.fee"),
      // Сравнение с нулём, а не вычисление (правило ТЗ №5).
      value: moneySign(robot.data.profit_fee_pct) > 0
        ? formatPercent(robot.data.profit_fee_pct)
        : t("buy.terms.feeNone"),
    },
    { label: t("buy.terms.withdraw"), value: t("buy.terms.withdrawValue") },
  ];

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <header className="flex flex-col gap-[3px]">
        <h1 className="text-h2 font-semibold text-text-primary">{t("buy.confirmTitle")}</h1>
        <p className="text-micro font-normal text-text-secondary">{t("buy.step", { current: 2 })}</p>
      </header>

      <section
        className="flex flex-col gap-2 rounded-[22px] border bg-background-elevated p-[18px]"
        style={{ borderColor: color }}
      >
        <span className="flex items-start gap-2.5">
          <RobotAvatar robot={robot.data} size={34} />
          <span className="text-h3 font-semibold text-text-primary">
            {robot.data.name} · {t(`robots.market.${asMarket(robot.data.market)}`)}
          </span>
        </span>
        <p className="text-body font-semibold text-text-primary">
          {t("buy.chargeOff")} {formatPrice(amount, { suffix: "USDT" })}
        </p>
        <p className="text-caption font-normal text-text-secondary">
          {t(`robots.risk.${asRiskLevel(robot.data.risk_level)}`)} ·{" "}
          {t("buy.autoPause", { dd: formatPercent(robot.data.declared_max_dd) })}
        </p>
      </section>

      <dl className="flex flex-col rounded-[18px] border border-border-subtle bg-background-raised text-caption">
        {terms.map((row, index) => (
          <div
            key={row.label}
            className={[
              "flex items-start justify-between p-[13px]",
              index > 0 ? "border-t border-border-subtle" : "",
            ].join(" ")}
          >
            <dt className="font-normal text-text-secondary">{row.label}</dt>
            <dd className="font-semibold text-text-primary">{row.value}</dd>
          </div>
        ))}
      </dl>

      {amountError === undefined ? null : (
        <p role="alert" className="text-caption font-medium text-status-loss">
          {amountError}
        </p>
      )}

      <section className="rounded-[18px] border border-border-subtle bg-background-elevated p-[14px]">
        <Checkbox checked={accepted} onChange={setAccepted}>
          {t("buy.consent")}
        </Checkbox>
      </section>

      {/* `commit`, а не обычный отклик CTA: это последний шаг покупки, после
          которого списываются деньги (FE-14). */}
      <Button
        size="cta"
        block
        haptic="commit"
        disabled={!accepted}
        loading={buy.isPending}
        onClick={submit}
      >
        {buy.isPending ? t("buy.submitting") : t("robots.launch", { name: robot.data.name })}
      </Button>
    </div>
  );
}
